/**
 * Find Time — user-facing option set.
 *
 * Matches legacy wording exactly so the backend `schedule_type`
 * strings round-trip cleanly (see `../state/reducers.ts` +
 * payload builder).
 */

/**
 * The discriminant the form stores. `null` = "user has not picked a
 * Find Time option" (custom mode). Any other value is one of the
 * legacy strings the backend accepts.
 */
export type FindTimeValue =
  | null
  /** Main button action — fill every weekday in the current week
   *  (today through Sunday), regardless of where the calendar is
   *  currently navigated. */
  | 'this_week'
  | 'next_weekday'
  | 'next_2_weekdays'
  | 'next_3_weekdays'
  | 'next_4_weekdays'
  | 'next_5_weekdays'
  | 'next_week'
  | 'next_2_weeks';

export interface FindTimeMode {
  value: Exclude<FindTimeValue, null>;
  /** Base label — the renderer may append a dynamic range. */
  baseLabel: string;
  /** Legacy backend string — sent in payload as `schedule_type`. */
  scheduleType: string;
}

/**
 * The five "next N weekdays" modes + the two "next week(s)" modes.
 * Order matches the Figma dropdown (top-to-bottom).
 */
export const FIND_TIME_MODES: FindTimeMode[] = [
  {
    value: 'next_weekday',
    baseLabel: 'Next weekday',
    scheduleType: 'Next weekday',
  },
  {
    value: 'next_2_weekdays',
    baseLabel: 'Next 2 weekdays',
    scheduleType: 'Next 2 weekdays',
  },
  {
    value: 'next_3_weekdays',
    baseLabel: 'Next 3 weekdays',
    scheduleType: 'Next 3 weekdays',
  },
  {
    value: 'next_4_weekdays',
    baseLabel: 'Next 4 weekdays',
    scheduleType: 'Next 4 weekdays',
  },
  {
    value: 'next_5_weekdays',
    baseLabel: 'Next 5 weekdays',
    scheduleType: 'Next 5 weekdays',
  },
  {
    value: 'next_week',
    baseLabel: 'Next week',
    scheduleType: 'Next week',
  },
  {
    value: 'next_2_weeks',
    baseLabel: 'Next 2 weeks',
    scheduleType: 'Next 2 weeks',
  },
];

/** Number of target weekdays for each `next_N_weekdays` mode. */
export function weekdaysTargetFor(value: FindTimeValue): number | null {
  switch (value) {
    case 'next_weekday':
      return 1;
    case 'next_2_weekdays':
      return 2;
    case 'next_3_weekdays':
      return 3;
    case 'next_4_weekdays':
      return 4;
    case 'next_5_weekdays':
      return 5;
    default:
      return null;
  }
}

/** Map any option back to its legacy backend `schedule_type` string. */
export function scheduleTypeFor(value: FindTimeValue): string {
  if (value === null) return 'Custom';
  // `this_week` was never a backend type (legacy only had the
  // next_* variants), so payload-wise we treat it as Custom — the
  // backend just sees a flat `time_slots` list, same as manual
  // click-created picks. Matches legacy parity.
  if (value === 'this_week') return 'Custom';
  return (
    FIND_TIME_MODES.find((m) => m.value === value)?.scheduleType ?? 'Custom'
  );
}
